import { 
  Database, 
  Shield, 
  FileText, 
  AlertCircle, 
  Download,
  Upload,
  Hash,
  CheckCircle2,
  XCircle
} from 'lucide-react';

const ResultsTab = ({ results, downloadSBOM, resetAnalysis }) => {
  const toTitleCase = (value) => {
    if (value === null || value === undefined) return '';
    return String(value)
      .replace(/[_-]+/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'text-red-400';
      case 'high': return 'text-red-400';
      case 'medium': return 'text-yellow-400';
      case 'warning': return 'text-yellow-400';
      case 'low': return 'text-blue-400';
      case 'info': return 'text-gray-400';
      default: return 'text-gray-400';
    }
  };

  const formatDuration = (duration) => {
    if (duration === null || duration === undefined) return 'N/A';
    if (duration < 1) {
      return `${Math.round(duration * 1000)}ms`;
    }
    return `${duration.toFixed(2)}s`;
  };

  const formatBytes = (bytes) => {
    if (bytes === null || bytes === undefined) return 'N/A';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileName = (path) => {
    return path.split('\\').pop() || path.split('/').pop() || path;
  };

  const getCheckSuccessRate = () => {
    if (!results?.total_checks) return 0;
    return Math.round((results.passed_checks / results.total_checks) * 100);
  };

  const formatUnixTimestamp = (value) => {
    if (!value) return 'N/A';
    const ms = Number(value) < 1e12 ? Number(value) * 1000 : Number(value);
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleString();
  };

  const statusStyles = {
    passed: 'text-green-400 bg-green-500/10 border-green-500/30',
    failed: 'text-red-400 bg-red-500/10 border-red-500/30',
    warning: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30',
    info: 'text-blue-400 bg-blue-500/10 border-blue-500/30',
  };

  const getStatusClass = (status) => statusStyles[status?.toLowerCase()] || 'text-gray-400 bg-gray-500/10 border-gray-500/30';

  const nonPassedChecks = (results?.checks || []).filter(
    (check) => (check?.status || '').toLowerCase() !== 'passed'
  );

  const formatLicenseInfo = (licenseInfo) => {
    if (!Array.isArray(licenseInfo) || licenseInfo.length === 0) {
      return 'None detected';
    }

    const visibleLicenses = licenseInfo.map((item) => {
      if (typeof item === 'string') {
        return item;
      }

      if (item && typeof item === 'object') {
        if (typeof item.confidence === 'number' && item.confidence <= 0.5) {
          return null;
        }
        const primary = item.spdx_id || item.name || 'Unknown license';
        return primary;
      }

      return null;
    }).filter(Boolean);

    return visibleLicenses.length > 0 ? visibleLicenses.join(', ') : 'None detected';
  };

  const renderIssueDetails = (details) => {
    if (!details || typeof details !== 'object') {
      return null;
    }

    const hiddenKeys = new Set(['impact', 'files', 'total_count']);
    const entries = Object.entries(details).filter(([key]) => !hiddenKeys.has(key));
    if (entries.length === 0) {
      return null;
    }

    return (
      <div className="mt-3">
        <span className="text-gray-400 text-sm">Details:</span>
        <div className="mt-2 space-y-2">
          {entries.map(([key, value]) => (
            <div key={key} className="bg-black/30 border border-gray-800 rounded p-2">
              <p className="text-xs uppercase tracking-wide text-gray-400">{key.replaceAll('_', ' ')}</p>
              {typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? (
                <p className="text-sm text-gray-200 break-words">{String(value)}</p>
              ) : (
                <pre className="mt-1 text-xs text-gray-300 overflow-auto">{JSON.stringify(value, null, 2)}</pre>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (!results) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-400">No results to display</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Scan Overview */}
      <div className="bg-[#1a1a1c] border border-[#232325] rounded-xl p-6">
        <h3 className="text-xl font-semibold mb-6 flex items-center gap-3 text-white">
          <Database className="w-6 h-6 text-blue-400" />
          Scan Overview
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-6">
          <div className="text-center bg-black/30 p-4 rounded-lg">
            <div className="text-3xl font-bold text-blue-400">{results.files_scanned}</div>
            <div className="text-sm text-gray-400 mt-2">Files Scanned</div>
          </div>
          <div className="text-center bg-black/30 p-4 rounded-lg">
            <div className="text-3xl font-bold text-green-400">{formatBytes(results.bytes_scanned)}</div>
            <div className="text-sm text-gray-400 mt-2">Data Processed</div>
          </div>
          <div className="text-center bg-black/30 p-4 rounded-lg">
            <div className="text-3xl font-bold text-purple-400">{formatDuration(results.duration)}</div>
            <div className="text-sm text-gray-400 mt-2">Scan Duration</div>
          </div>
          <div className="text-center bg-black/30 p-4 rounded-lg">
            <div className="text-lg font-bold text-gray-200 break-words">{formatUnixTimestamp(results.start_time)}</div>
            <div className="text-sm text-gray-400 mt-2">Start Time</div>
          </div>
        </div>

        <div className="mt-6">
          <p className="text-sm text-gray-400 mb-2">Scanner Names</p>
          {results.scanner_names && results.scanner_names.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {results.scanner_names.map((name, i) => (
                <span key={i} className="px-3 py-1 bg-gray-700/50 border border-gray-600 rounded-full text-sm text-gray-200">
                  {name}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No scanner names reported</p>
          )}
        </div>
      </div>

      {/* Check Results */}
      <div className="bg-[#1a1a1c] border border-[#232325] rounded-xl p-6">
        <h3 className="text-xl font-semibold mb-6 flex items-center gap-3 text-white">
          <Shield className="w-6 h-6 text-blue-400" />
          Check Results
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center bg-black/30 p-4 rounded-lg">
            <div className="text-4xl font-bold text-blue-400">{results.total_checks}</div>
            <div className="text-sm text-gray-400 mt-2">Total Checks</div>
          </div>
          <div className="text-center bg-black/30 p-4 rounded-lg">
            <div className="text-4xl font-bold text-green-400">{results.passed_checks}</div>
            <div className="text-sm text-gray-400 mt-2">Passed</div>
          </div>
          <div className="text-center bg-black/30 p-4 rounded-lg">
            <div className="text-4xl font-bold text-red-400">{results.failed_checks}</div>
            <div className="text-sm text-gray-400 mt-2">Failed</div>
          </div>
          <div className="text-center bg-black/30 p-4 rounded-lg">
            <div className="text-4xl font-bold text-yellow-400">{getCheckSuccessRate()}%</div>
            <div className="text-sm text-gray-400 mt-2">Success Rate</div>
          </div>
        </div>
      </div>

      {/* Detailed Checks */}
      <div className="bg-[#1a1a1c] border border-[#232325] rounded-xl p-6">
        <h3 className="text-xl font-semibold mb-6 flex items-center gap-3 text-white">
          <CheckCircle2 className="w-6 h-6 text-blue-400" />
          Detailed Checks
        </h3>
        {nonPassedChecks.length > 0 ? (
          <div className="space-y-4">
            {nonPassedChecks.map((check, index) => (
              <div key={index} className="border border-gray-700/60 rounded-lg p-4 bg-black/20">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    {check.status?.toLowerCase() === 'passed' ? (
                      <CheckCircle2 className="w-5 h-5 text-green-400" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-400" />
                    )}
                    <p className="text-white font-semibold">{check.name || 'Unnamed Check'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {check.severity && (
                      <span className={`px-2 py-1 rounded border text-xs font-medium ${getSeverityColor(check.severity)} bg-gray-800/60 border-gray-700`}>
                        {toTitleCase(check.severity)}
                      </span>
                    )}
                    <span className={`px-2 py-1 rounded border text-xs font-medium ${getStatusClass(check.status)}`}>
                      {toTitleCase(check.status || 'unknown')}
                    </span>
                  </div>
                </div>

                <p className="text-gray-300 text-sm mb-2">{check.message || 'No message'}</p>
                <div className="text-xs text-gray-500 flex flex-wrap gap-4 mb-3">
                  <span>Location: {check.location || 'N/A'}</span>
                  <span>Timestamp: {formatUnixTimestamp(check.timestamp)}</span>
                </div>

                <details className="group">
                  <summary className="cursor-pointer text-sm text-blue-300 hover:text-blue-200">
                    View check details JSON
                  </summary>
                  <pre className="mt-2 text-xs text-gray-300 bg-black/40 border border-gray-800 rounded p-3 overflow-auto">
                    {JSON.stringify(check.details || {}, null, 2)}
                  </pre>
                </details>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No non-passed checks to display</p>
        )}
      </div>

      {/* Scanned Assets */}
      <div className="bg-[#1a1a1c] border border-[#232325] rounded-xl p-6">
        <h3 className="text-xl font-semibold mb-6 flex items-center gap-3 text-white">
          <FileText className="w-6 h-6 text-blue-400" />
          Scanned Assets
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-3 px-4 font-semibold text-gray-400">File Name</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-400">Type</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-400">Size</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-400">Classification</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-400">Keys</th>
              </tr>
            </thead>
            <tbody>
              {(results.assets || []).map((asset, index) => {
                const metadata = (results.file_metadata || {})[asset.path] || {};
                return (
                  <tr key={index} className="border-b border-gray-800 hover:bg-black/20">
                    <td className="py-3 px-4 font-medium text-white">
                      {getFileName(asset.path)}
                    </td>
                    <td className="py-3 px-4 text-gray-300">
                      <span className="px-2 py-1 bg-gray-700 rounded text-xs">
                        {asset.type}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-300">{formatBytes(asset.size || metadata?.file_size)}</td>
                    <td className="py-3 px-4 text-gray-300">
                      <div className="flex gap-2">
                        {metadata?.is_dataset && (
                          <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">
                            Dataset
                          </span>
                        )}
                        {metadata?.is_model && (
                          <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">
                            Model
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-300">
                      {Array.isArray(asset.keys) && asset.keys.length > 0 ? (
                        <details>
                          <summary className="cursor-pointer text-blue-300 hover:text-blue-200">
                            {asset.keys.length} keys
                          </summary>
                          <div className="mt-2 flex flex-wrap gap-1">
                            {asset.keys.map((keyName, keyIndex) => (
                              <span key={keyIndex} className="px-2 py-0.5 text-xs bg-gray-800 rounded text-gray-300 border border-gray-700">
                                {keyName}
                              </span>
                            ))}
                          </div>
                        </details>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Issues & Warnings */}
      {results.issues && results.issues.length > 0 && (
        <div className="bg-[#1a1a1c] border border-[#232325] rounded-xl p-6">
          <h3 className="text-xl font-semibold mb-6 flex items-center gap-3 text-white">
            <AlertCircle className="w-6 h-6 text-blue-400" />
            Issues & Warnings
          </h3>
          <div className="space-y-4">
            {results.issues.map((issue, index) => (
              <div key={index} className="border border-gray-700/70 rounded-lg p-4 bg-black/20">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-3">
                    <AlertCircle className={`w-5 h-5 ${getSeverityColor(issue.severity)}`} />
                    <span className="font-semibold text-lg text-white">{issue.message}</span>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSeverityColor(issue.severity)} bg-gray-800/60`}>
                    {toTitleCase(issue.severity)}
                  </span>
                </div>
                
                {issue.details?.impact && (
                  <div className="mb-3">
                    <span className="text-gray-400 text-sm">Impact:</span>
                    <p className="text-gray-300 text-sm mt-1">{issue.details.impact}</p>
                  </div>
                )}

                {issue.details?.files && (
                  <div className="mb-3">
                    <span className="text-gray-400 text-sm">
                      Affected Files ({issue.details.total_count ?? issue.details.files.length}):
                    </span>
                    <div className="mt-2 space-y-1">
                      {issue.details.files.map((file, fileIndex) => (
                        <div key={fileIndex} className="text-sm text-gray-300 bg-black/30 p-2 rounded">
                          {getFileName(file)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {renderIssueDetails(issue.details)}

                {issue.why && (
                  <div className="mt-3">
                    <span className="text-gray-400 text-sm">Why:</span>
                    <p className="text-gray-300 text-sm mt-1">{issue.why}</p>
                  </div>
                )}

                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span>Type: {issue.type}</span>
                  {issue.timestamp && (
                    <span>Time: {formatUnixTimestamp(issue.timestamp)}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* File Metadata Details */}
      <div className="bg-[#1a1a1c] border border-[#232325] rounded-xl p-6">
        <h3 className="text-xl font-semibold mb-6 flex items-center gap-3 text-white">
          <Hash className="w-6 h-6 text-blue-400" />
          File Metadata
        </h3>
        <div className="space-y-4">
          {Object.entries(results.file_metadata || {}).map(([path, metadata], index) => (
            <div key={index} className="border border-gray-700/50 rounded-lg p-4 bg-black/10">
              <div className="flex justify-between items-start mb-3">
                <h4 className="font-medium text-white text-lg">{getFileName(path)}</h4>
                <span className="text-xs text-gray-500">
                  {formatUnixTimestamp(metadata.scan_timestamp)}
                </span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">File Size:</span>
                  <p className="text-gray-300 mt-1">{formatBytes(metadata.file_size)}</p>
                </div>
                <div>
                  <span className="text-gray-400">Risk Score:</span>
                  <p className="text-gray-300 mt-1">{metadata.risk_score ?? 'N/A'}</p>
                </div>
                <div>
                  <span className="text-gray-400">Is Dataset:</span>
                  <p className="text-gray-300 mt-1">{metadata.is_dataset ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <span className="text-gray-400">Is Model:</span>
                  <p className="text-gray-300 mt-1">{metadata.is_model ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <span className="text-gray-400">Content Hash:</span>
                  <p className="text-gray-300 font-mono text-xs break-all mt-1">
                    {metadata.content_hash || 'N/A'}
                  </p>
                </div>
                <div>
                  <span className="text-gray-400">License Info:</span>
                  <p className="text-gray-300 mt-1">
                    {formatLicenseInfo(metadata.license_info)}
                  </p>
                </div>
              </div>

              {metadata.license_files_nearby?.length > 0 && (
                <div className="mt-3">
                  <span className="text-gray-400 text-sm">Nearby License Files:</span>
                  <div className="mt-1 space-y-1">
                    {metadata.license_files_nearby.map((licenseFile, idx) => (
                      <p key={idx} className="text-gray-300 text-sm">{licenseFile}</p>
                    ))}
                  </div>
                </div>
              )}

              {metadata.keys?.length > 0 && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-sm text-blue-300 hover:text-blue-200">
                    View Parsed Keys ({metadata.keys.length})
                  </summary>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {metadata.keys.map((keyName, keyIndex) => (
                      <span key={keyIndex} className="px-2 py-0.5 text-xs bg-gray-800 rounded text-gray-300 border border-gray-700">
                        {keyName}
                      </span>
                    ))}
                  </div>
                </details>
              )}

              {metadata.model_info && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-sm text-blue-300 hover:text-blue-200">
                    View Model Info
                  </summary>
                  <pre className="mt-2 text-xs text-gray-300 bg-black/40 border border-gray-800 rounded p-3 overflow-auto">
                    {JSON.stringify(metadata.model_info, null, 2)}
                  </pre>
                </details>
              )}

              {metadata.ml_context && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-sm text-blue-300 hover:text-blue-200">
                    View ML Context
                  </summary>
                  <pre className="mt-2 text-xs text-gray-300 bg-black/40 border border-gray-800 rounded p-3 overflow-auto">
                    {JSON.stringify(metadata.ml_context, null, 2)}
                  </pre>
                </details>
              )}

              {metadata.copyright_notices?.length > 0 && (
                <div className="mt-3">
                  <span className="text-gray-400 text-sm">Copyright Notices:</span>
                  <div className="mt-1">
                    {metadata.copyright_notices.map((notice, noticeIndex) => (
                      <p key={noticeIndex} className="text-gray-300 text-sm">{notice}</p>
                    ))}
                  </div>
                </div>
              )}

              <details className="mt-3">
                <summary className="cursor-pointer text-sm text-blue-300 hover:text-blue-200">
                  View Full File Metadata JSON
                </summary>
                <pre className="mt-2 text-xs text-gray-300 bg-black/40 border border-gray-800 rounded p-3 overflow-auto">
                  {JSON.stringify(metadata, null, 2)}
                </pre>
              </details>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-4 mt-8">
        <button
          onClick={downloadSBOM}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors font-semibold text-white"
        >
          <Download className="w-5 h-5" />
          Download Report
        </button>
        <button
          onClick={resetAnalysis}
          className="flex items-center gap-2 px-6 py-3 bg-gray-700 hover:bg-gray-800 rounded-lg transition-colors font-semibold text-white"
        >
          <Upload className="w-5 h-5" />
          New Analysis
        </button>
      </div>
    </div>
  );
};

export default ResultsTab;
